import { supabase } from "@/integrations/supabase/client";
import { uploadAttachment } from "@/services/attachmentService";

export interface BugReportInsert {
  user_id?: string | null;
  email: string;
  category: string;
  severity: string;
  description: string;
  page_url?: string;
  browser?: string;
  os?: string;
  device_type?: string;
  viewport?: string;
  console_errors?: any[];
  app_version?: string;
  subscription_plan?: string | null;
  screenshots?: string[];
  honeypot?: string;
  session_metadata?: Record<string, any>;
}

export interface BugReport {
  id: string;
  ticket_number: number;
  user_id: string | null;

  email: string;
  category: string;
  severity: string;
  status: string;
  description: string;
  page_url: string | null;
  browser: string | null;
  os: string | null;
  device_type: string | null;
  viewport: string | null;
  console_errors: any;
  app_version: string | null;
  subscription_plan: string | null;
  screenshots: string[];
  merged_into: string | null;
  assigned_to: string | null;
  admin_notes: string | null;
  session_metadata: Record<string, any> | null;
  created_at: string;
  updated_at: string;
}

export interface BugReportReply {
  id: string;
  bug_report_id: string;
  user_id: string;
  is_admin: boolean;
  message: string;
  created_at: string;
}

/** Submit a new bug report */
export async function submitBugReport(report: BugReportInsert): Promise<{ id: string }> {
  const { data, error } = await supabase
    .from("bug_reports" as any)
    .insert(report as any)
    .select("id")
    .single();
  if (error) throw error;
  return data as any;
}

/** Upload a bug-report screenshot to R2 and return the public cdn URL. */
export async function uploadBugScreenshot(
  file: Blob,
  fileName: string,
): Promise<string> {
  return uploadAttachment("bug-screenshots", file, fileName);
}

/** Get bug reports for the current user */
export async function getUserBugReports(): Promise<BugReport[]> {
  const { data, error } = await supabase
    .from("bug_reports" as any)
    .select("*")
    .order("created_at", { ascending: false });
  if (error) throw error;
  return (data || []) as unknown as BugReport[];
}

/** Get all bug reports (admin) */
export async function getAllBugReports(): Promise<BugReport[]> {
  const { data, error } = await supabase
    .from("bug_reports" as any)
    .select("*")
    .is("merged_into", null)
    .order("created_at", { ascending: false });
  if (error) throw error;
  return (data || []) as unknown as BugReport[];
}

/** Update a bug report (admin) */
export async function updateBugReport(
  id: string,
  updates: Partial<Pick<BugReport, "status" | "assigned_to" | "admin_notes" | "merged_into">>
) {
  const { error } = await supabase
    .from("bug_reports" as any)
    .update(updates as any)
    .eq("id", id);
  if (error) throw error;
}

/** Get replies for a bug report */
export async function getBugReportReplies(bugReportId: string): Promise<BugReportReply[]> {
  const { data, error } = await supabase
    .from("bug_report_replies" as any)
    .select("*")
    .eq("bug_report_id", bugReportId)
    .order("created_at", { ascending: true });
  if (error) throw error;
  return (data || []) as unknown as BugReportReply[];
}

/** Add a reply to a bug report */
export async function addBugReportReply(
  bugReportId: string,
  userId: string,
  message: string,
  isAdmin: boolean
) {
  const { error } = await supabase
    .from("bug_report_replies" as any)
    .insert({
      bug_report_id: bugReportId,
      user_id: userId,
      is_admin: isAdmin,
      message,
    } as any);
  if (error) throw error;
}
